import { useEffect, useState, type TransitionEvent } from 'react';

/**
 * Safety net only — unmounts even if `onExitTransitionEnd` never fires (e.g.
 * `prefers-reduced-motion` drops the CSS transition entirely, so no
 * `transitionend` event exists to drive it). Generous on purpose: the real
 * exit is paced by the browser finishing the transition, not this timer.
 */
const FALLBACK_UNMOUNT_DELAY_MS = 400;

/**
 * Keeps a conditionally-rendered node mounted through its CSS exit transition
 * instead of unmounting the instant `shouldShow` turns false. The node's own
 * `transitionend` event — wired through `onExitTransitionEnd` — drives the
 * actual unmount, so it can never be cut off early by a fixed timer racing the
 * animation; a generous fallback timeout only covers the case where the
 * transition never fires at all. `isVisible` lags `shouldShow` by two frames
 * on the way in, giving the CSS a starting state to transition from.
 */
export const useMountTransition = (shouldShow: boolean) => {
  const [shouldRender, setShouldRender] = useState(shouldShow);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (shouldShow) {
      setShouldRender(true);
      // A single rAF often lands in the same paint as the node's insertion —
      // the browser then has no "hidden" frame on screen to transition from,
      // so the badge just snaps to visible. Nesting one more rAF guarantees a
      // real paint of the hidden state happens first.
      let innerFrameId = 0;
      const frameId = requestAnimationFrame(() => {
        innerFrameId = requestAnimationFrame(() => setIsVisible(true));
      });
      return () => {
        cancelAnimationFrame(frameId);
        cancelAnimationFrame(innerFrameId);
      };
    }

    setIsVisible(false);
    const timeoutId = setTimeout(
      () => setShouldRender(false),
      FALLBACK_UNMOUNT_DELAY_MS,
    );
    return () => clearTimeout(timeoutId);
  }, [shouldShow]);

  const onExitTransitionEnd = (event: TransitionEvent<HTMLElement>) => {
    if (!shouldShow && event.target === event.currentTarget) {
      setShouldRender(false);
    }
  };

  return { isVisible, onExitTransitionEnd, shouldRender };
};
